import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Icons from "../icons";
import { statusOf } from "../testStatus";
import { stepToEnglish, stepToScript } from "../stepEnglish";

const ACTIONS = [
  "fill",
  "type",
  "click",
  "press",
  "selectOption",
  "expectVisible",
  "expectText",
  "expectUrlContains",
  "expectDisabled",
  "expectEnabled",
  "expectFocused",
  "expectNotFocused",
  "expectValue",
  "pickDate",
];

/* Circular status glyph (per row / per step). */
function StatusGlyph({ status, className = "h-4 w-4" }) {
  const s = statusOf(status);
  const Glyph = s.icon;
  return (
    <span className={["inline-flex flex-none items-center justify-center rounded-full", s.solid].join(" ")}>
      <Glyph className={[className, status === "running" ? "animate-spin" : ""].join(" ")} />
    </span>
  );
}

export function StatusBadge({ status }) {
  const s = statusOf(status);
  const Glyph = s.icon;
  return (
    <span
      className={[
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset",
        s.badge,
      ].join(" ")}
    >
      <Glyph className={["h-3.5 w-3.5", status === "running" ? "animate-spin" : ""].join(" ")} />
      {s.label}
    </span>
  );
}

// Colour-code the priority pill so High/Medium/Low read at a glance.
// Palette follows the StatusBadge convention in testStatus.js
// (bg-*-50 / text-*-700 / ring-*-600/20) for visual consistency.
function priorityChip(p) {
  const key = String(p || "").toLowerCase();
  if (key === "high" || key === "critical")
    return "bg-rose-50 text-rose-700 ring-rose-600/20";
  if (key === "low")
    return "bg-slate-100 text-slate-600 ring-slate-500/20";
  return "bg-amber-50 text-amber-700 ring-amber-600/20";
}

function MiniIndicators({ steps }) {
  const passed = steps.filter((s) => s.status === "passed").length;
  return (
    <div className="flex items-center gap-2" title="Step results">
      <div className="flex max-w-[140px] flex-wrap items-center gap-1">
        {steps.map((step, i) => (
          <span
            key={i}
            className={[
              "h-1.5 w-4 rounded-full transition-colors",
              statusOf(step.status).dot,
              step.status === "running" ? "animate-pulse" : "",
            ].join(" ")}
          />
        ))}
      </div>
      <span className="whitespace-nowrap text-xs text-slate-400">
        {passed}/{steps.length}
      </span>
    </div>
  );
}

// Xray import mapping: for a test case authored from an Xray Test, show each
// pre-written Xray step (Action / Data / Expected) alongside the Playwright
// step(s) authored against it (grouped by source_step_no).
/* A Cucumber test's steps are its scenario's lines: the API parses the
   Gherkin into steps carrying a `keyword` (Given / When / Then / And / But). */
export const isGherkin = (steps) => (steps || []).some((s) => s && s.keyword);

/* A Cucumber scenario drawn like Jira's scenario editor: line number, the
   keyword in blue, then the text. A data table or doc-string under a line
   (`data`) shows beneath it in mono. `compact` is for narrow columns. */
export function GherkinLines({ steps, compact = false }) {
  return (
    <ol className={`font-mono ${compact ? "text-[11px]" : "text-[13px]"} leading-relaxed`}>
      {(steps || []).map((s, i) => (
        <li key={s.step_no || i + 1} className={`flex gap-3 ${compact ? "py-0.5" : "px-4 py-1"}`}>
          <span className="w-5 flex-none select-none text-right text-slate-400 tabular-nums">{s.step_no || i + 1}</span>
          <span className="min-w-0 flex-1 break-words">
            <span className="font-semibold text-blue-700">{s.keyword}</span>{" "}
            <span className="text-slate-800">{s.action}</span>
            {s.data && (
              <span className="mt-0.5 block whitespace-pre-wrap text-slate-500">{s.data}</span>
            )}
          </span>
        </li>
      ))}
    </ol>
  );
}

/* Scenario lines only — what the "N steps" counts show. A Manual test's
   Action / Data / Expected steps are not shown (they live in How To Test). */
export const scenarioCount = (steps) => (isGherkin(steps) ? steps.length : 0);

export const NO_SCENARIO = "No Cucumber scenario on this test in Jira.";

/* A test's Cucumber scenario under a "Test Steps" heading — shared by the
   Requirement Board's Review screen and the Create Artifact screen. Only the
   scenario is shown; a test without one says so. */
export function XrayTestSteps({ steps }) {
  return (
    <div>
      <p className="border-b border-slate-200 px-4 py-2 text-xs font-bold uppercase tracking-wider text-slate-800">
        Test Steps
      </p>
      {isGherkin(steps) ? (
        <div className="py-2">
          <GherkinLines steps={steps} />
        </div>
      ) : (
        <p className="px-4 py-5 text-center text-xs text-slate-400">{NO_SCENARIO}</p>
      )}
    </div>
  );
}

export function XrayMappingGrid({
  steps,
  xraySteps,
  onOpenScreenshot,
  showHeader = true,
  /* false drops the outer border + bottom margin, for callers that already
     sit the grid inside their own card. */
  framed = true,
}) {
  /* Some callers pass Xray steps straight from Xray, where step_no can be
     missing — fall back to the 1-based position, as the step tables do. */
  const rowsX = (xraySteps || []).map((xs, i) => ({ ...xs, step_no: xs.step_no || i + 1 }));
  const known = new Set(rowsX.map((xs) => xs.step_no));
  /* A Playwright step pointing at an Xray step that no longer exists (the
     Xray test was edited after automation) goes to the "not tied" row rather
     than silently disappearing. */
  const byStep = (steps || []).reduce((acc, s) => {
    const n = s.source_step_no ?? 0;
    const k = known.has(n) ? n : 0;
    (acc[k] = acc[k] || []).push(s);
    return acc;
  }, {});
  /* Each Playwright step is shown as a sentence a tester can read, with the
     raw instruction tucked underneath in brackets for debugging. The status
     dot, error line and screenshot button live on the same row so the
     per-step card list that used to sit beneath this grid is no longer
     needed — everything a reviewer wants is right next to the Xray step. */
  const renderPwSteps = (arr) => (
    <ul className="space-y-1.5">
      {arr.map((s, si) => {
        const script = stepToScript(s);
        return (
          <li key={si} className="flex items-start gap-1.5">
            <span
              className={["mt-1.5 h-1.5 w-1.5 flex-none rounded-full", statusOf(s.status).dot].join(" ")}
            />
            <div className="min-w-0 flex-1">
              <div className="text-slate-700">{stepToEnglish(s)}</div>
              {script && (
                <div className="mt-0.5 break-all font-mono text-[10px] text-slate-400">
                  ({script})
                </div>
              )}
              {s.error && (
                <div className="mt-1 rounded border border-rose-200 bg-rose-50 px-1.5 py-0.5 font-mono text-[10px] text-rose-700">
                  {s.error}
                </div>
              )}
            </div>
            {s.screenshot && (
              <button
                type="button"
                onClick={() =>
                  onOpenScreenshot?.({
                    src: s.screenshot,
                    caption: stepToEnglish(s),
                  })
                }
                title="Load screenshot"
                className="flex-none inline-flex items-center gap-1 rounded border border-slate-200 bg-card px-1.5 py-0.5 text-[10px] font-medium text-slate-500 transition hover:border-brand-500 hover:text-brand-700"
              >
                <Icons.Eye className="h-3 w-3" />
                Screenshot
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
  return (
    <div className={framed ? "mb-4 overflow-hidden rounded-lg border border-violet-200" : "overflow-hidden"}>
      {showHeader && (
        <div className="flex items-center gap-1.5 border-b border-violet-100 bg-violet-50 px-3 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-violet-700">
          <span className="rounded-full bg-violet-100 px-1.5 py-0.5 text-[9px] tracking-normal">
            Test
          </span>
          Step mapping (Test step → Artifact)
        </div>
      )}
      <table className="w-full table-fixed border-collapse text-left text-xs">
        <thead>
          <tr className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wider text-slate-500">
            <th className="w-10 p-2">#</th>
            <th className="w-2/5 p-2">Test step (Action / Data / Expected)</th>
            <th className="p-2">Artifact steps</th>
          </tr>
        </thead>
        <tbody>
          {rowsX.map((xs) => {
            const mapped = byStep[xs.step_no] || [];
            return (
              <tr key={xs.step_no} className="border-t border-slate-100 align-top">
                <td className="p-2 text-slate-400">{xs.step_no}</td>
                <td className="p-2">
                  <div className="text-slate-800">{xs.action}</div>
                  {xs.data ? (
                    <div className="mt-0.5 text-slate-500">
                      <span className="font-medium">Data:</span> {xs.data}
                    </div>
                  ) : null}
                  {xs.result ? (
                    <div className="mt-0.5 text-slate-500">
                      <span className="font-medium">Expected:</span> {xs.result}
                    </div>
                  ) : null}
                </td>
                <td className="p-2">
                  {mapped.length ? (
                    renderPwSteps(mapped)
                  ) : (
                    <span className="italic text-amber-600">No Artifact step found.</span>
                  )}
                </td>
              </tr>
            );
          })}
          {byStep[0]?.length ? (
            <tr className="border-t border-slate-100 bg-slate-50/50 align-top">
              <td className="p-2 text-slate-400">—</td>
              <td className="p-2 italic text-slate-500">Steps not tied to a test step</td>
              <td className="p-2">{renderPwSteps(byStep[0])}</td>
            </tr>
          ) : null}
        </tbody>
      </table>
    </div>
  );
}

function ActionButton({ icon: Glyph, label, onClick, primary = false, danger = false, spin = false, disabled = false }) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={label}
      aria-label={label}
      className={[
        "inline-flex h-8 w-8 items-center justify-center rounded-lg border text-sm transition disabled:cursor-not-allowed disabled:opacity-50",
        primary
          ? "border-brand-600 bg-brand-600 text-white hover:bg-brand-700"
          : danger
          ? "border-rose-200 bg-card text-rose-500 hover:border-rose-300 hover:bg-rose-50"
          : "border-slate-200 bg-card text-slate-500 hover:border-slate-300 hover:text-slate-800",
      ].join(" ")}
    >
      <Glyph className={["h-4 w-4", spin ? "animate-spin" : ""].join(" ")} />
    </button>
  );
}

export function ScreenshotLightbox({ src, caption, onClose }) {
  // Close on Escape.
  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-6"
      role="dialog"
      aria-modal="true"
    >
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />
      <div className="relative z-10 flex max-h-full max-w-6xl flex-col overflow-hidden rounded-xl bg-slate-900 shadow-2xl ring-1 ring-slate-700">
        <div className="flex items-center justify-between border-b border-slate-700 px-4 py-2 text-sm text-slate-200">
          <span className="truncate">{caption}</span>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close screenshot"
            className="rounded-md p-1 text-slate-400 hover:bg-slate-800 hover:text-slate-100"
          >
            <Icons.X className="h-5 w-5" />
          </button>
        </div>
        <div className="min-h-0 flex-1 overflow-auto bg-black">
          <img src={src} alt={caption} className="mx-auto block h-auto max-w-full" />
        </div>
      </div>
    </div>
  );
}

/* Renders the validation errors the app itself displayed after a primary-
 * action click. Populated from the `errors_detected` WebSocket event. */
function DetectedErrorsList({ errors }) {
  if (!errors || !errors.length) return null;
  return (
    <div className="mt-2 rounded-md border border-rose-200 bg-rose-50 p-2">
      <div className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-rose-700">
        App rejected — {errors.length} validation error{errors.length === 1 ? "" : "s"} visible
      </div>
      <ul className="space-y-1">
        {errors.map((e, idx) => (
          <li key={idx} className="text-[11px] text-rose-800">
            <span className="mr-1">•</span>
            {e.field && (
              <span className="font-medium">{e.field}</span>
            )}
            {e.field && <span className="text-rose-500"> · </span>}
            <span>“{e.text}”</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

function StepCard({ step, index, active, onOpenScreenshot }) {
  const s = statusOf(step.status);
  const border =
    step.status === "passed"
      ? "border-emerald-200"
      : step.status === "failed"
      ? "border-rose-300"
      : step.status === "running"
      ? "border-amber-300"
      : "border-slate-200";
  return (
    <li
      className={[
        "rounded-lg border bg-card p-3 transition",
        border,
        active ? "ring-2 ring-amber-200" : "",
      ].join(" ")}
    >
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-6 w-6 flex-none items-center justify-center rounded-full bg-slate-100 text-[11px] font-semibold text-slate-600">
          {index + 1}
        </span>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <StatusGlyph status={step.status} className="h-3.5 w-3.5" />
            <span className={["text-sm font-medium", s.text].join(" ")}>{s.label}</span>
            {step.action && (
              <span className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-slate-600">
                {step.action}
              </span>
            )}
          </div>
          <p className="mt-1 truncate text-sm text-slate-700" title={step.name}>
            {step.name}
          </p>
          {step.detectedErrors && <DetectedErrorsList errors={step.detectedErrors} />}
          {step.error && (
            <p className="mt-2 rounded-md border border-rose-200 bg-rose-50 px-2 py-1 font-mono text-[11px] text-rose-700">
              {step.error}
            </p>
          )}
        </div>
        {step.screenshot && (
          <button
            type="button"
            onClick={() =>
              onOpenScreenshot?.({
                src: step.screenshot,
                caption: `Step ${index + 1}: ${step.name}`,
              })
            }
            className="flex-none inline-flex items-center gap-1.5 self-start rounded-md border border-slate-200 bg-card px-2.5 py-1.5 text-xs font-medium text-slate-600 transition hover:border-brand-500 hover:text-brand-700"
            title="Load screenshot from database"
          >
            <Icons.Eye className="h-3.5 w-3.5" />
            View screenshot
          </button>
        )}
      </div>
    </li>
  );
}

// Actions that consume a `value` at run time. Anything not in this set (e.g.
// click, expectVisible, expectHidden, expectDisabled, expectEnabled) has its
// value input disabled so users don't try to fill something the runner will
// ignore.
const VALUE_ACTIONS = new Set([
  "fill",
  "type",
  "press",
  "selectOption",
  "selectDropdown",
  "expectText",
  "expectUrlContains",
  "expectValue",
  "pickDate",
]);

function EditableStepRow({ step, onChange, onDelete }) {
  const takesValue = VALUE_ACTIONS.has(step.action);
  const isDynamic = !!step.is_dynamic;
  const isPassword = (step.kind || "").toLowerCase() === "password" || !!step.masked;
  // Password values are masked in the editor to match the field they were
  // captured from; the eye button flips to plaintext so the tester can
  // verify the recorded value.
  const [showPassword, setShowPassword] = useState(false);
  // Dynamic steps get their value from the datasource or a run-time prompt,
  // so the input is read-only and displays the param placeholder.
  const valueDisabled = !takesValue || isDynamic;
  const valueDisplay = isDynamic
    ? `{{${step.param_name || "value"}}}`
    : step.value ?? "";
  const valuePlaceholder = !takesValue
    ? "(not used by this action)"
    : isDynamic
    ? "(prompted at run time)"
    : "value";
  const captureOn = !!step.capture_screenshot;

  return (
    <tr className="border-b border-slate-100 last:border-b-0">
      {/* Action / Step name / Selector are read-only in the editor: they were
          captured by the recorder against the running app and rewriting them
          by hand would silently break the test. If they're wrong, re-record
          the affected step. Only Value + the row options are user-editable. */}
      <td className="p-2">
        <input
          value={step.action || "click"}
          disabled
          readOnly
          title="Action is captured by the recorder — re-record to change it"
          className="w-full cursor-not-allowed rounded-md border border-slate-200 bg-slate-100 px-2 py-1 font-mono text-xs text-slate-500 outline-none"
        />
      </td>
      <td className="p-2">
        <input
          value={step.description || ""}
          onChange={(e) => onChange({ ...step, description: e.target.value })}
          placeholder="(no name)"
          title="Step name — free-text label shown in the run view"
          className="w-full rounded-md border border-slate-300 bg-card px-2 py-1 text-xs text-slate-800 outline-none focus:border-brand-600"
        />
      </td>
      <td className="p-2">
        <input
          value={step.selector || ""}
          onChange={(e) => onChange({ ...step, selector: e.target.value })}
          placeholder="selector"
          title="Selector — edit only if you know what you're doing (bad selectors break replay)"
          className="w-full rounded-md border border-slate-300 bg-card px-2 py-1 font-mono text-xs text-slate-800 outline-none focus:border-brand-600"
        />
      </td>
      <td className="p-2">
        <div className="relative">
          <input
            type={isPassword && !showPassword && !isDynamic ? "password" : "text"}
            value={valueDisplay}
            onChange={(e) => onChange({ ...step, value: e.target.value })}
            placeholder={valuePlaceholder}
            disabled={valueDisabled}
            autoComplete={isPassword ? "new-password" : undefined}
            title={
              isDynamic
                ? "Dynamic — value is supplied by the datasource or run-time prompt"
                : !takesValue
                ? "This action does not use a value"
                : undefined
            }
            className={[
              "w-full rounded-md border px-2 py-1 font-mono text-xs outline-none focus:border-brand-600",
              isPassword && !valueDisabled ? "pr-7" : "",
              valueDisabled
                ? "cursor-not-allowed border-slate-200 bg-slate-100 text-slate-500"
                : "border-slate-300 bg-card text-slate-800",
            ].join(" ")}
          />
          {isPassword && !isDynamic && (
            <button
              type="button"
              onClick={() => setShowPassword((v) => !v)}
              tabIndex={-1}
              title={showPassword ? "Hide password" : "Show password"}
              className="absolute inset-y-0 right-1 flex items-center rounded px-1 text-slate-400 hover:text-slate-600"
            >
              <Icons.Eye className="h-3.5 w-3.5" />
            </button>
          )}
        </div>
      </td>
      <td className="p-2 text-right">
        <div className="inline-flex items-center gap-1">
          <button
            type="button"
            onClick={() => onChange({ ...step, capture_screenshot: !captureOn })}
            title={
              captureOn
                ? "Screenshot ON — click to disable capture for this step"
                : "Screenshot OFF — click to capture a screenshot for this step"
            }
            className={[
              "rounded p-1 transition",
              captureOn
                ? "bg-emerald-50 text-emerald-600 ring-1 ring-emerald-200 hover:bg-emerald-100"
                : "text-slate-400 hover:bg-slate-100 hover:text-slate-600",
            ].join(" ")}
          >
            <Icons.Camera className="h-3.5 w-3.5" />
          </button>
          <button
            type="button"
            onClick={onDelete}
            className="rounded-md p-1 text-rose-500 hover:bg-rose-50"
            title="Remove step"
          >
            <Icons.X className="h-4 w-4" />
          </button>
        </div>
      </td>
    </tr>
  );
}

export default function TestCaseRow({
  testCase,
  /* What to print as the row's title. Defaults to the case name. Callers that
     already show the name directly above the row — the Requirement Board's
     story screen, where a Playwright case is named after its Xray test — pass
     a short label instead so the same sentence is not printed twice. Only the
     display changes; edit mode still edits the real name. */
  title,
  isExpanded,
  isRunning,
  onToggleExpand,
  onRun,
  onStop,
  onView,
  onProperties,
  onSaveEdits,
  onDelete,
  onHistory,
  selectable = false,
  selected = false,
  onToggleSelect,
  onUngroup,
  datasources = [],
  /* View-only rendering (the Requirement Board's story screen). Shows the
     script, its last-run status and the step mapping, but none of the
     actions: no Run, View, Properties, Delete or Edit steps, no priority chip
     and no "Step mapping" banner. Only expand / collapse remains. */
  readOnly = false,
}) {
  const { name, priority, status, steps, source } = testCase;
  const passed = steps.filter((s) => s.status === "passed").length;

  const [editing, setEditing] = useState(false);
  const [dsMenuOpen, setDsMenuOpen] = useState(false);
  // dsMenuRef wraps BOTH the chevron button AND the portalled menu so the
  // outside-click handler can tell an "inside" click from an "outside" one.
  // dsButtonRef is only the chevron button — used to position the menu.
  const dsMenuRef = useRef(null);
  const dsButtonRef = useRef(null);
  const dsPopupRef = useRef(null);
  const [dsMenuPos, setDsMenuPos] = useState({ top: 0, left: 0, minWidth: 0 });

  // Position the menu right below the chevron button. Recomputed on open and
  // whenever the viewport scrolls or resizes so it stays aligned.
  useLayoutEffect(() => {
    if (!dsMenuOpen) return undefined;
    const place = () => {
      const btn = dsButtonRef.current;
      if (!btn) return;
      const rect = btn.getBoundingClientRect();
      const menuWidth = 256; // matches w-64 (16rem)
      // Right-align to the chevron button's right edge, clamp to viewport.
      const left = Math.max(8, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 8));
      setDsMenuPos({ top: rect.bottom + 4, left, minWidth: menuWidth });
    };
    place();
    window.addEventListener("scroll", place, true); // capture scrolls inside any container
    window.addEventListener("resize", place);
    return () => {
      window.removeEventListener("scroll", place, true);
      window.removeEventListener("resize", place);
    };
  }, [dsMenuOpen]);

  // Close the datasource picker on outside click / Escape.
  useEffect(() => {
    if (!dsMenuOpen) return undefined;
    const onClick = (e) => {
      const inWrapper = dsMenuRef.current && dsMenuRef.current.contains(e.target);
      const inPopup = dsPopupRef.current && dsPopupRef.current.contains(e.target);
      if (!inWrapper && !inPopup) setDsMenuOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setDsMenuOpen(false);
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [dsMenuOpen]);
  const [draftName, setDraftName] = useState(name);
  const [draftSteps, setDraftSteps] = useState(steps);
  const [lightbox, setLightbox] = useState(null);

  // Reset the draft when the underlying case changes (e.g., after reload).
  useEffect(() => {
    if (!editing) {
      setDraftName(name);
      setDraftSteps(steps);
    }
  }, [name, steps, editing]);

  const startEdit = () => {
    setDraftName(name);
    setDraftSteps(steps.map((s) => ({ ...s })));
    setEditing(true);
  };

  const cancelEdit = () => {
    setDraftName(name);
    setDraftSteps(steps);
    setEditing(false);
  };

  const commitEdit = async () => {
    await onSaveEdits?.(testCase.id, draftName.trim() || name, draftSteps);
    setEditing(false);
  };

  return (
    <>
      {/* Main row */}
      <div
        className={[
          "group",
          isExpanded ? "bg-brand-50/40" : "hover:bg-slate-50",
        ].join(" ")}
      >
        <div className="flex flex-wrap items-center gap-x-4 gap-y-3 px-4 py-3">
          {/* Multi-select checkbox (only shown in grouping/select mode) */}
          {selectable && (
            <input
              type="checkbox"
              checked={selected}
              onChange={() => onToggleSelect?.(testCase.id)}
              onClick={(e) => e.stopPropagation()}
              aria-label={selected ? "Deselect test case" : "Select test case"}
              className="h-4 w-4 flex-none cursor-pointer rounded border-slate-300 text-brand-600 focus:ring-brand-600/40"
            />
          )}
          {/* Test case */}
          <div className="flex min-w-0 flex-1 basis-64 items-center gap-3">
            <StatusGlyph status={status} />
            <div className="min-w-0">
              <div className="flex items-start gap-2">
                <span className="break-words font-medium text-slate-900" title={name}>
                  {title || name}
                </span>
                {!readOnly && (
                  <span
                    className={[
                      "mt-0.5 flex-none rounded-full px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ring-1 ring-inset",
                      priorityChip(priority),
                    ].join(" ")}
                  >
                    {priority}
                  </span>
                )}
              </div>
              <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600">
                {testCase.menuLabel && (
                  <span className="truncate font-medium text-slate-700">{testCase.menuLabel}</span>
                )}
                {testCase.createdBy && (
                  <span className="inline-flex items-center gap-1">
                    <Icons.Users className="h-3.5 w-3.5 text-slate-400" />
                    <span className="truncate">{testCase.createdBy}</span>
                  </span>
                )}
                {testCase.createdVia === "manual" && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2 py-0.5 text-[11px] font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20">
                    <Icons.Pencil className="h-3 w-3" />
                    Manual
                  </span>
                )}
                {testCase.createdVia === "ai" && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 px-2 py-0.5 text-[11px] font-medium text-violet-700 ring-1 ring-inset ring-violet-600/20">
                    <Icons.Spark className="h-3 w-3" />
                    AI
                  </span>
                )}
                {(testCase.linkedTickets || []).map((t) => (
                  <a
                    key={t.ticketkey}
                    href={t.url || undefined}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    title={t.summary ? `${t.ticket_key} — ${t.summary}` : t.ticket_key}
                    className="inline-flex items-center gap-1 rounded-full bg-brand-50 px-2 py-0.5 text-[11px] font-mono font-medium text-brand-700 ring-1 ring-inset ring-brand-600/20 hover:bg-brand-100"
                  >
                    <Icons.Boards className="h-3 w-3" />
                    {t.ticket_key}
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Status */}
          <div className="flex w-28 flex-none justify-start">
            <StatusBadge status={status} />
          </div>

          {/* Results */}
          <div className="w-44 flex-none">
            <MiniIndicators steps={steps} />
          </div>

          {/* Actions */}
          <div className="flex flex-none items-center gap-1.5">
            {/* Run: single button when no datasources; split button (main
                click = prompt for values, dropdown = pick a saved dataset)
                when at least one dataset exists for this case. */}
            {!readOnly && (<>
            {isRunning && (
              <ActionButton
                icon={Icons.X}
                label="Stop run"
                danger
                onClick={() => onStop?.(testCase.id)}
              />
            )}
            {datasources.length === 0 ? (
              <ActionButton
                icon={isRunning ? Icons.Spinner : status === "pending" ? Icons.Play : Icons.Refresh}
                label={isRunning ? "Running…" : status === "pending" ? "Run" : "Run again"}
                primary
                spin={isRunning}
                disabled={isRunning || steps.length === 0}
                onClick={() => !isRunning && onRun?.(testCase.id)}
              />
            ) : (
              <div className="relative inline-flex" ref={dsMenuRef}>
                <button
                  type="button"
                  disabled={isRunning || steps.length === 0}
                  onClick={() => !isRunning && onRun?.(testCase.id, null)}
                  title={isRunning ? "Running…" : "Run and prompt for values"}
                  className="inline-flex h-8 items-center gap-1 rounded-l-lg border border-r-0 border-brand-600 bg-brand-600 px-2 text-sm text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {isRunning ? (
                    <Icons.Spinner className="h-4 w-4 animate-spin" />
                  ) : status === "pending" ? (
                    <Icons.Play className="h-4 w-4" />
                  ) : (
                    <Icons.Refresh className="h-4 w-4" />
                  )}
                </button>
                <button
                  ref={dsButtonRef}
                  type="button"
                  disabled={isRunning || steps.length === 0}
                  onClick={() => setDsMenuOpen((o) => !o)}
                  aria-haspopup="menu"
                  aria-expanded={dsMenuOpen}
                  title="Run with a saved Datasource"
                  className="inline-flex h-8 items-center rounded-r-lg border border-l-0 border-brand-600 bg-brand-600 px-1.5 text-white transition hover:bg-brand-700 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <Icons.ChevronDown className="h-3.5 w-3.5" />
                </button>
                {dsMenuOpen &&
                  createPortal(
                    <div
                      ref={dsPopupRef}
                      role="menu"
                      style={{
                        position: "fixed",
                        top: dsMenuPos.top,
                        left: dsMenuPos.left,
                        minWidth: dsMenuPos.minWidth,
                        zIndex: 60,
                      }}
                      className="overflow-hidden rounded-lg border border-slate-200 bg-white shadow-lg"
                    >
                      <button
                        type="button"
                        role="menuitem"
                        onClick={() => {
                          setDsMenuOpen(false);
                          onRun?.(testCase.id, null);
                        }}
                        className="flex w-full items-center gap-2 border-b border-slate-100 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                      >
                        <Icons.Play className="h-4 w-4 text-slate-400" />
                        Prompt for values
                      </button>
                      {datasources.map((d) => {
                        // Legacy iteration stored a matrix as `{schema, rows}`;
                        // count vars from row 0 in that case so the sub-label
                        // stays accurate for old datasources.
                        const rows = Array.isArray(d?.data?.rows) ? d.data.rows : null;
                        const varCount = rows
                          ? Object.keys(rows[0]?.values || {}).length
                          : Object.keys(d.data || {}).length;
                        return (
                          <button
                            key={d.datasourcekey}
                            type="button"
                            role="menuitem"
                            onClick={() => {
                              setDsMenuOpen(false);
                              onRun?.(testCase.id, d.datasourcekey);
                            }}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-slate-700 hover:bg-slate-50"
                          >
                            <Icons.Grid className="h-4 w-4 text-slate-400" />
                            <span className="truncate">{d.name}</span>
                            <span className="ml-auto text-[10px] text-slate-400">
                              {varCount} variables
                            </span>
                          </button>
                        );
                      })}
                    </div>,
                    document.body
                  )}
              </div>
            )}
            <ActionButton icon={Icons.Eye} label="View" onClick={() => onView?.(testCase)} />
            {onHistory && (
              <ActionButton
                icon={Icons.Clock}
                label="Run history"
                onClick={() => onHistory?.(testCase)}
              />
            )}
            <ActionButton icon={Icons.Settings} label="Properties" onClick={() => onProperties?.(testCase)} />
            {onUngroup && testCase.groupKey && (
              <ActionButton
                icon={Icons.Minus}
                label="Remove from sub-category"
                onClick={() => onUngroup?.(testCase.id)}
              />
            )}
            {/* Not offered when its runs are kept (For you, or recorded in Jira). */}
            {onDelete && !testCase.deleteBlocked && (
              <ActionButton icon={Icons.Trash} label="Delete" danger onClick={() => onDelete?.(testCase.id)} />
            )}
            {onDelete && testCase.deleteBlocked && (
              <span title={testCase.deleteBlockedReason} className="inline-flex items-center gap-1 px-1.5 text-[11px] text-slate-400">
                <Icons.Lock className="h-3.5 w-3.5" aria-hidden="true" />
                <span className="sr-only">Can't be deleted: </span>
                Kept
              </span>
            )}
            </>)}
            <ActionButton
              icon={isExpanded ? Icons.ChevronUp : Icons.ChevronDown}
              label={isExpanded ? "Collapse" : "Expand"}
              onClick={() => onToggleExpand?.(testCase.id)}
            />
          </div>
        </div>

        {/* Expanded detail */}
        {isExpanded && (
          <div className="px-4 pb-4 pt-0">
            <div className="rounded-xl border border-slate-200 bg-card p-4">
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                <div className="flex items-center gap-2">
                  <StatusBadge status={status} />
                  <span className="text-xs text-slate-500">
                    {passed}/{steps.length} steps passed
                  </span>
                  {testCase.menuPath?.length > 0 && (
                    <span className="text-xs text-slate-400">
                      · {testCase.menuPath.join(" > ")}
                    </span>
                  )}
                </div>
                <div className="flex items-center gap-1.5">
                  {!editing && onSaveEdits && !readOnly && (
                    <button
                      type="button"
                      onClick={startEdit}
                      disabled={isRunning}
                      className="inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
                    >
                      <Icons.Settings className="h-3.5 w-3.5" />
                      Edit steps
                    </button>
                  )}
                  {editing && (
                    <>
                      <button
                        type="button"
                        onClick={cancelEdit}
                        className="rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50"
                      >
                        Cancel
                      </button>
                      <button
                        type="button"
                        onClick={commitEdit}
                        className="rounded-md bg-brand-700 px-2 py-1 text-xs font-semibold text-white hover:bg-brand-800"
                      >
                        Save
                      </button>
                    </>
                  )}
                  <button
                    type="button"
                    onClick={() => onToggleExpand?.(testCase.id)}
                    className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs font-medium text-slate-500 hover:bg-slate-100 hover:text-slate-700"
                  >
                    <Icons.ChevronUp className="h-4 w-4" />
                    Collapse
                  </button>
                </div>
              </div>

              {testCase.lastRun?.error && (
                <div className="mb-3 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                  {testCase.lastRun.error}
                </div>
              )}

              {editing ? (
                <div className="space-y-3">
                  <div>
                    <label className="mb-1 block text-xs font-medium text-slate-600">Name</label>
                    <input
                      value={draftName}
                      onChange={(e) => setDraftName(e.target.value)}
                      className="w-full rounded-md border border-slate-300 bg-card px-2 py-1.5 text-sm text-slate-900 outline-none focus:border-brand-600"
                    />
                  </div>
                  <table className="w-full table-fixed border-collapse text-left">
                    <thead>
                      <tr className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                        <th className="w-32 p-2">Action</th>
                        <th className="w-64 p-2">Step name</th>
                        <th className="p-2">Selector</th>
                        <th className="p-2">Value</th>
                        <th className="w-20 p-2 text-right">Options</th>
                      </tr>
                    </thead>
                    <tbody>
                      {draftSteps.map((s, i) => (
                        <EditableStepRow
                          key={i}
                          step={s}
                          onChange={(ns) =>
                            setDraftSteps((prev) => prev.map((x, xi) => (xi === i ? ns : x)))
                          }
                          onDelete={() =>
                            setDraftSteps((prev) => prev.filter((_, xi) => xi !== i))
                          }
                        />
                      ))}
                    </tbody>
                  </table>
                  {/* No "Add step" here: action/selector/step-name are locked
                      to what the recorder captured, so a manually-added blank
                      row could never be filled in usefully. Re-record the
                      test case to add new steps. */}
                </div>
              ) : steps.length === 0 ? (
                <p className="text-sm text-slate-500">No steps in this test case.</p>
              ) : (
                <>
                  {source?.type === "xray" &&
                    Array.isArray(source.xray_steps) &&
                    source.xray_steps.length > 0 ? (
                      /* Xray-sourced tests: the mapping grid above now carries
                         the English sentence, bracketed script, status dot,
                         error and screenshot for each Playwright step — the
                         per-step card list below used to repeat the action
                         verb and selector, so it's dropped here. */
                      <XrayMappingGrid
                        steps={steps}
                        xraySteps={source.xray_steps}
                        onOpenScreenshot={setLightbox}
                        showHeader={!readOnly}
                      />
                    ) : (
                      <ol className="space-y-2">
                        {steps.map((step, i) => (
                          <StepCard
                            key={i}
                            step={step}
                            index={i}
                            active={step.status === "running"}
                            onOpenScreenshot={setLightbox}
                          />
                        ))}
                      </ol>
                    )}
                </>
              )}
            </div>
          </div>
        )}
      </div>

      {lightbox && (
        <ScreenshotLightbox
          src={lightbox.src}
          caption={lightbox.caption}
          onClose={() => setLightbox(null)}
        />
      )}
    </>
  );
}
